import { DOCUMENT_CHANGE_TOOL_NAMES } from "@common/constants/tools";

import type { DocumentBackup } from "./api";

const SECONDS_PER_HOUR = 3600;
const MILLISECONDS_PER_SECOND = 1000;
const MILLISECONDS_PER_HOUR = SECONDS_PER_HOUR * MILLISECONDS_PER_SECOND;
const BACKUP_NAME_CLOCK_PATTERN = /^\d{8}T\d{2}(\d{2})(\d{2})(\d{6})~/;
const ISO_CLOCK_PATTERN = /T\d{2}:(\d{2}):(\d{2})(?:\.(\d+))?/;

export interface PlaceableStep {
  toolCallId: string;
  toolName: string;
  startedAt: number;
}

export interface PlaceableRow {
  id: string;
  turnType: "user" | "assistant";
  messageId?: string;
  toolName?: string;
  timestamp?: string;
  createdAt?: string;
  steps?: PlaceableStep[];
}

interface ChipOwner {
  slotKey: string;
  id: string;
  messageId?: string;
  isPrompt: boolean;
  editsDocument: boolean;
  exchange: number;
  secondsIntoHour: number | null;
  instantMs: number | null;
}

export function rowSlotKey(rowId: string): string {
  return `row:${rowId}`;
}

export function stepSlotKey(toolCallId: string): string {
  return `step:${toolCallId}`;
}

function secondsIntoHour(minutes: string, seconds: string, fraction: string | undefined): number {
  return Number(minutes) * 60 + Number(seconds) + Number(`0.${fraction ?? "0"}`);
}

function checkpointSecondsIntoHour(backupName: string): number | null {
  const clock = BACKUP_NAME_CLOCK_PATTERN.exec(backupName);
  return clock ? secondsIntoHour(clock[1], clock[2], clock[3]) : null;
}

function isoSecondsIntoHour(isoTimestamp: string): number | null {
  const clock = ISO_CLOCK_PATTERN.exec(isoTimestamp);
  return clock ? secondsIntoHour(clock[1], clock[2], clock[3]) : null;
}

function secondsForward(from: number, to: number): number {
  return (((to - from) % SECONDS_PER_HOUR) + SECONDS_PER_HOUR) % SECONDS_PER_HOUR;
}

function rowOwner(row: PlaceableRow, exchange: number): ChipOwner {
  const rowTimestamp = row.timestamp ?? row.createdAt;
  const instantMs = rowTimestamp ? Date.parse(rowTimestamp) : NaN;
  return {
    slotKey: rowSlotKey(row.id),
    id: row.id,
    messageId: row.messageId,
    isPrompt: row.turnType === "user",
    editsDocument: !!row.toolName && DOCUMENT_CHANGE_TOOL_NAMES.has(row.toolName),
    exchange,
    secondsIntoHour: rowTimestamp ? isoSecondsIntoHour(rowTimestamp) : null,
    instantMs: Number.isNaN(instantMs) ? null : instantMs,
  };
}

function stepOwner(step: PlaceableStep, exchange: number): ChipOwner {
  return {
    slotKey: stepSlotKey(step.toolCallId),
    id: step.toolCallId,
    isPrompt: false,
    editsDocument: DOCUMENT_CHANGE_TOOL_NAMES.has(step.toolName),
    exchange,
    secondsIntoHour: (step.startedAt % MILLISECONDS_PER_HOUR) / MILLISECONDS_PER_SECOND,
    instantMs: step.startedAt,
  };
}

function collectOwners(rows: PlaceableRow[]): ChipOwner[] {
  const rowIds = new Set(rows.map((row) => row.id));
  const owners: ChipOwner[] = [];
  let exchange = 0;
  for (const row of rows) {
    if (row.turnType === "user") exchange += 1;
    owners.push(rowOwner(row, exchange));
    for (const step of row.steps ?? []) {
      if (!rowIds.has(step.toolCallId)) owners.push(stepOwner(step, exchange));
    }
  }
  return owners;
}

function isClocked(owner: ChipOwner): boolean {
  return owner.secondsIntoHour !== null && owner.instantMs !== null;
}

function chronologicalOrder(owners: ChipOwner[]): number[] {
  return owners
    .map((_, index) => index)
    .filter((index) => isClocked(owners[index]))
    .sort((left, right) => owners[left].instantMs! - owners[right].instantMs! || left - right);
}

function findOwnerNamed(owners: ChipOwner[], anchorId: string): number {
  return owners.findIndex((owner) => owner.messageId === anchorId || owner.id === anchorId);
}

function findOwnerByClock(
  owners: ChipOwner[],
  order: number[],
  checkpointSeconds: number,
  exchange: number | null,
): number {
  const scoped = exchange === null ? order : order.filter((index) => owners[index].exchange === exchange);
  let claimed = -1;
  for (let position = scoped.length - 1; position >= 1; position--) {
    const openedAt = owners[scoped[position - 1]].secondsIntoHour!;
    const spanSeconds = secondsForward(openedAt, owners[scoped[position]].secondsIntoHour!);
    const offsetSeconds = secondsForward(openedAt, checkpointSeconds);
    if (offsetSeconds > 0 && offsetSeconds <= spanSeconds) {
      if (claimed < 0) claimed = scoped[position];
      else if (owners[scoped[position]].exchange !== owners[claimed].exchange) return -1;
    }
  }
  return claimed;
}

function findTriggerOwner(
  owners: ChipOwner[],
  order: number[],
  backup: DocumentBackup,
): number {
  const named = backup.turn_id ? findOwnerNamed(owners, backup.turn_id) : -1;
  if (backup.tool_call_id) {
    const triggered = findOwnerNamed(owners, backup.tool_call_id);
    if (triggered >= 0) return triggered;
  } else if (named >= 0 && named + 1 < owners.length) {
    return named + 1;
  }
  const checkpointSeconds = checkpointSecondsIntoHour(backup.name);
  if (checkpointSeconds === null) return -1;
  return findOwnerByClock(owners, order, checkpointSeconds, named >= 0 ? owners[named].exchange : null);
}

function chronologicalRanks(owners: ChipOwner[], order: number[]): Map<number, number> {
  return new Map(order.map((ownerIndex, rank) => [ownerIndex, rank]));
}

function findHostOwner(owners: ChipOwner[], order: number[], trigger: number): number {
  const { exchange } = owners[trigger];
  const ranks = chronologicalRanks(owners, order);
  const triggerRank = ranks.get(trigger);
  const isUndone = (index: number): boolean => {
    const ownerRank = ranks.get(index);
    return ownerRank === undefined || triggerRank === undefined
      ? index >= trigger
      : ownerRank >= triggerRank;
  };

  let lastSurviving = -1;
  let firstUndone = owners.length;
  owners.forEach((owner, index) => {
    if (owner.exchange !== exchange || !owner.editsDocument) return;
    if (isUndone(index)) firstUndone = Math.min(firstUndone, index);
    else lastSurviving = Math.max(lastSurviving, index);
  });

  const exchangeStart = owners.findIndex((owner) => owner.exchange === exchange);
  let host = Math.max(lastSurviving + 1, exchangeStart);
  if (host > firstUndone) host = firstUndone;
  if (host > trigger) host = trigger;
  while (host < owners.length && owners[host].isPrompt) host += 1;
  return host < owners.length ? host : -1;
}

export function resolveBackupPlacements(
  backupsNewestFirst: DocumentBackup[],
  rows: PlaceableRow[],
): Map<string, DocumentBackup> {
  const owners = collectOwners(rows);
  const order = chronologicalOrder(owners);
  const placements = new Map<string, DocumentBackup>();
  let searchLimit = owners.length - 1;
  for (const backup of backupsNewestFirst) {
    const trigger = findTriggerOwner(owners, order, backup);
    if (trigger < 0) continue;
    const host = findHostOwner(owners, order, trigger);
    if (host < 0 || host > searchLimit) continue;
    const { slotKey } = owners[host];
    if (!placements.has(slotKey)) placements.set(slotKey, backup);
    searchLimit = host;
  }
  return placements;
}
